<template lang="pug">
.carousel.flex.flex-col.fade-in(
  :data-completed="screen.id ? suspend.completed[screen.id] : null"
  :id="props.screen.id"
  ref="elRef"
)
  tab-group.overflow-hidden.flex.flex-col.py-6.gap-4.bg-paper(
    :as="ucMaxHAvailable"
    :onChange="open"
    :selectedIndex="currentIndex"
  )
    tab-list.flex.mx-auto.justify-center.items-center(
      :class="{ hidden: screen.slides.length === 1 }"
      uc-word-skip=""
    )
      tab-button(
        as="template"
        v-for="(tab, i) in screen.slides"
        v-slot="{ selected }"
      )
        //- expose these strings for the translate script
        //- text="Slide"
        button.group.flex-0.flex.flex-col.items-center.justify-center.text-center.rounded-full.focus-visible_ring-4.transition-colors.p-2(
          :class="[selected ? ' z-2 text-primary-600' : 'text-gray-200 hover_text-primary-200']"
          :data-dot="tab.id"
          :aria-label="`${hasTranslation('Slide')} ${i + 1}`"
        )
          uc-icon.text-sm(icon="mdi:circle")

    tab-panels.flex-1.overflow-hidden(as="div")
      .collapsed-items.max-h-full.overflow-hidden
        .carousel-slide.collapsed-item.p-2.md_px-4.pb-0.flex.flex-col.gap-10(
          :class="[i === currentIndex ? ' carousel-slide--active visibile' : 'invisible']"
          :key="tab.text"
          v-for="(tab, i) in screen.slides"
        )
          UseLockables(
            :status-ref="addSlideLockableStatus"
            :id="screen.id && getSlideId(i)"
            v-slot="{ locked: slideLocked }"
          )
            .flex-1.overflow-y-auto.overflow-x-hidden.simple-scrollbar.p-2.md_px-4
              audio-marker(:name="getSlideId(i)")
              uc-preload-sfx(name="continue")
              uc-screen(
                :nested="true"
                :edit-path="`${editPath}.slides.${i}`"
                :screen="tab"
                v-if="tab.template || (Array.isArray(tab.templates) && tab.templates.length > 0)"
              )
                .text-end.w-full(
                  :class="[slideLocked ? 'auto-alpha-0' : 'auto-alpha-1']"
                  v-if="tab.template"
                )
                  uc-button.btn.btn-primary(
                    :class="[smallDevice && 'w-full']"
                    @click="next"
                    data-sfx="continue"
                    text="Next"
                    v-if="i !== screen.slides.length - 1"
                  )

              uc-content(
                :edit-path="`${editPath}.slides.${i}`"
                :screen="{ h: 'h4', center: !(tab.img || tab['img-left'] || tab['img-right']), ...tab }"
                v-else=""
              )
            //- expose these strings for the translate script
            //- text="Previous Slide"
            //- text="Next Slide"
            .flex.flex-0.justify-center.gap-20.mx-auto.leading-none.text-2xl.text-primary-600(
              :class="{ hidden: screen.slides.length === 1 }"
              uc-word-skip=""
            )
              uc-button.btn-icon.ripple.ripple--centered(
                :class="{ 'saturate-0 opacity-25': disablePrev }"
                :disabled="disablePrev"
                :aria-label="hasTranslation('Previous Slide')"
                @click="prev"
              ) 
                uc-icon.dark_text-white.rtl_-scale-x-100(icon="theme:left")

              uc-button.btn-icon.ripple.ripple--centered(
                :class="{ 'saturate-0 opacity-25': disableNext }"
                :disabled="slideLocked || disableNext"
                :aria-label="hasTranslation('Next Slide')"
                @click="next"
              ) 
                uc-icon.dark_text-white.rtl_-scale-x-100(icon="theme:right")
</template>

<script setup lang="ts">
import {
  ref,
  unref,
  onMounted,
  nextTick,
  computed,
  inject,
  watch,
  toRef,
  withDefaults,
  type Ref
} from 'vue';
import {
  TabGroup,
  TabList,
  Tab as TabButton,
  TabPanels
} from '@headlessui/vue';
import {
  setViewed,
  getViewed,
  setCompleted,
  getCompleted,
  set
} from '../lib/SCORM';
import { play, stop, playUrlSequence } from '../lib/audio';
import useScreenAudio, {
  resolveSlideAudioUrls,
  type NarratableScreen
} from '../lib/useScreenAudio';
import carouselSchema from '../lib/screen-schemas/screen-carousel.schema';
import useSuspend from '../lib/useSuspend';
import useTranslations from '../stores/useTranslations';
import {
  useCompletables,
  UseLockables,
  useLockables
} from '../stores/useLockables';
import bugAlert from '../lib/bugAlert';
import ucMaxHAvailable from './uc-max-h-available.vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import isQuizPage from '../lib/isQuizPage';

interface CarouselHTMLElement extends HTMLDivElement {
  next: () => void;
  prev: () => void;
}

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: ScreenCarousel;
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);

const currentIndex = ref(0);
const elRef = ref<CarouselHTMLElement>();
const suspend = useSuspend();
const { translations } = useTranslations();
const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');

// locked until all completables on slides are completed
const sectionId = inject('sectionId') as string;
const pageId = inject('pageId') as object;
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));

if (!props.screen.id) {
  bugAlert(`Carousel missing an id in #${sectionId}`);
}

useCompletables(props.screen.id);
const { locked } = useLockables(props.screen.id);

const getSlideId = (i: number) => `${props.screen.id}-slide-${i}`;

const slideLockables = ref(new Map<string, LockableStatus>());

const addSlideLockableStatus = (status: LockableStatus) => {
  slideLockables.value.set(status.id, status);
};

// for disabling prev at the start
const disablePrev = computed(() => currentIndex.value === 0);

// for disabling next at the end
const disableNext = computed(
  () => currentIndex.value === props.screen.slides.length - 1
);

const { resolveSlot } = useScreenAudio(toRef(props, 'screen'), carouselSchema);

/** Slide row audio: `templates[]` chain, split panels, single nested `template`, or the carousel's own `slide-N` slot (plain title/text). */
const resolveSlideUrlList = (i: number): Promise<string[]> => {
  const slide = props.screen.slides[i];
  if (!slide) return Promise.resolve([]);

  return resolveSlideAudioUrls(
    slide as NarratableScreen,
    props.screen as unknown as NarratableScreen,
    {
      slideId: slide.id ?? getSlideId(i),
      editPathBase: `${props.screen.id}.slides.${i}`,
      resolvePlainSlide: () => resolveSlot(`slide-${i}`)
    }
  );
};

const checkCompletion = () => {
  const { id } = props.screen;

  const allViewed = props.screen.slides.every(({ label }, i) =>
    getViewed(getViewedId(i) || getCompleted(getViewedId(i)))
  );

  if (allViewed && !locked.value) {
    if (id) setCompleted(id);
    setCompleted(sectionId);
  }

  if (!id) {
    setCompleted(sectionId);
  }
};

const getViewedId = (i: number) => {
  const slide = props.screen.slides[i];
  const label = slide.label || i;
  return `tab ${props.screen.id} ${label}`;
};

watch(locked, () => {
  checkCompletion();
});

const open = async (i: number, { ignoreAudio = false } = {}) => {
  const slide = props.screen.slides[i];
  const id = getSlideId(i);

  currentIndex.value = i;

  setViewed(getViewedId(i));

  if (slideLockables.value.get(id)?.locked.value === false) {
    setCompleted(id);
  }

  checkCompletion();

  if (!ignoreAudio) {
    stop();
    const propAudio = slide.audio || slide.speech;
    if (propAudio) {
      play(propAudio, { owner: props.screen.id });
    } else {
      const urls = await resolveSlideUrlList(i);
      // Bail if the user moved to a different slide while JiT was synthesizing.
      if (currentIndex.value !== i) return;
      playUrlSequence(urls, { owner: props.screen.id });
    }
  }

  nextTick().then(() => {
    elRef.value
      ?.querySelector<HTMLElement>(`[data-dot="${slide.id}"]`)
      ?.focus();
  });
};

const next = () => {
  const i = (currentIndex.value + 1) % props.screen.slides.length;
  open(i);
};

const prev = async () => {
  const i =
    (currentIndex.value + props.screen.slides.length - 1) %
    props.screen.slides.length;

  open(i);
};

function hasTranslation(key: string | number, fallback: string | number = key) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];
  return maybeTranslated || key;
}

// Schema audio on scroll enter: play the carousel intro then the active slide.
watch(sectionActive, async active => {
  if (props.nested) return;
  if (!active) return;

  const i = currentIndex.value;
  const slide = props.screen.slides[i];
  const propAudio = slide?.audio || slide?.speech;

  const introUrl = await resolveSlot('intro');
  const slideUrls = propAudio ? [propAudio] : await resolveSlideUrlList(i);

  // Bail if the section deactivated or the slide changed while resolving (JiT
  // synthesis can take seconds).
  if (!sectionActive.value || currentIndex.value !== i) return;

  // Play the first clip and queue the rest. The carousel's own intro is often
  // empty (heading/text live on the slides, e.g. split panels), so we must
  // actively play() the first slide clip rather than only queueing behind the
  // intro — otherwise nothing kicks the queue off on scroll enter.
  playUrlSequence([introUrl, ...slideUrls], { owner: props.screen.id });
});

onMounted(() => {
  if (!currentIndex.value) {
    void open(0, { ignoreAudio: true });
  }

  const el = unref(elRef);

  if (el) {
    el.next = next;
    el.prev = prev;
  }
});

const app = document.querySelector('#app');
app?.addEventListener('quizReset', async e => {
  if (await isQuizPage(e, pageId)) {
    props.screen.slides.forEach((slide, i) => {
      set(`viewed.${getViewedId(i)}`, false);
      set(`completed.${getViewedId(i)}`, false);
    });
  }
  void open(0, { ignoreAudio: true });
});
</script>

<style scoped>
/* .carousel {
  --auto-alpha-dur-0: 200ms;
}

.carousel-slide {
  transform: translateX(-20px);

  &--active ~ & {
    transform: translateX(20px);
  }

  &--active {
    transform: translateX(0) !important;
    opacity: 1;
    visibility: inherit;
    transition: opacity linear 0.5s;
  }
} */

:deep(.screen-next-button) {
  display: none;
}
</style>
